import { action, makeObservable } from 'mobx';
import {
    CoreFetch,
    DocumentApi,
    ProviderCreateDocumentType,
    SupportDocumentType,
} from '../api';

export interface ICoreDocumentsStoreConfig {
    supportGrant?: boolean;
}

export class CoreDocumentsStore {
    private config?: ICoreDocumentsStoreConfig;

    constructor(config?: ICoreDocumentsStoreConfig) {
        makeObservable(this);
        this.config = config;
    }

    private get isSupportApp(): boolean {
        return Boolean(this.config?.supportGrant);
    }

    @action
    public setSupportGrant = (support: boolean | undefined): void => {
        const nextSupportGrant = Boolean(support);
        if (this.config?.supportGrant === nextSupportGrant) {
            return;
        }
        this.config = { ...(this.config || {}), supportGrant: nextSupportGrant };
    };

    private uploadFileToS3 = (url: string, file: File): Promise<void> => {
        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.upload.addEventListener('load', () => resolve());
            xhr.upload.addEventListener('error', () => reject('S3 upload failed'));
            xhr.open('PUT', url);
            xhr.setRequestHeader('x-amz-server-side-encryption', 'aws:kms');
            xhr.overrideMimeType(file.type);
            xhr.send(file);
        });
    };

    public uploadDocumentFile = async (
        file: File,
        options?: {
            description?: string;
            documentType?: SupportDocumentType | ProviderCreateDocumentType;
            claimId?: string;
        },
    ): Promise<string> => {
        const docApi = await CoreFetch<DocumentApi>(DocumentApi);

        // 1) Create document
        const description = options?.description || file.name;
        const documentType = options?.documentType || (this.isSupportApp ? SupportDocumentType.MESSAGEDOCUMENT : ProviderCreateDocumentType.MESSAGEDOCUMENT);

        const created = this.isSupportApp
            ? await docApi.createSupportDocument({
                  supportCreateDocumentPayload: {
                      filename: file.name,
                      description,
                      documentType: documentType as SupportDocumentType,
                      claimId: options?.claimId,
                  },
              })
            : await docApi.createProviderDocument({
                  providerCreateDocumentPayload: {
                      filename: file.name,
                      documentType: documentType as ProviderCreateDocumentType,
                      description,
                      claimId: options?.claimId,
                  },
              });

        if (!created?.id) throw new Error('Failed to create document');

        // 2) Create upload
        const upload = this.isSupportApp
            ? await docApi.createSupportDocumentUpload({ id: created.id, body: {} })
            : await docApi.createProviderDocumentUpload({ id: created.id, body: {} });
        if (!upload?.url) throw new Error('Failed to create document upload');

        // 3) PUT file to S3
        await this.uploadFileToS3(upload.url, file);

        // 4) Refresh metadata
        if (this.isSupportApp) {
            await docApi.refreshDocumentMetadata({ id: created.id, body: {} });
        } else {
            await docApi.refreshProviderDocumentMetadata({ id: created.id, body: {} });
        }

        return created.id;
    };
}

export default CoreDocumentsStore;
